/**
 * rail-utils.js — 軌道交通 UI 輔助工具
 * 提供屬性轉換、圖示產生與樣式標準化函式
 */

/**
 * 取得垂直位階的中文標籤
 * @param {string} elevation - 原始值 (underground, elevated, at_grade, unknown)
 * @returns {string} 中文標籤
 */
export function getElevationLabel(elevation) {
    const map = {
        'underground': '地下',
        'elevated': '高架',
        'at_grade': '平面',
        'unknown': '未知位階'
    };
    return map[elevation] || '未知位階';
}

/**
 * 根據海拔與位階返回背景色與文字色
 * @param {string} elevation 
 * @returns {object} { bg, color }
 */
export function getElevationStyle(elevation) {
    const styles = {
        'underground': { bg: '#E1F5FE', color: '#01579B' },
        'elevated': { bg: '#FFF3E0', color: '#E65100' },
        'at_grade': { bg: '#F1F8E9', color: '#33691E' }
    };
    return styles[elevation] || { bg: '#F5F5F5', color: '#616161' };
}

const SVG = {
    ground: '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><rect x="1" y="7" width="14" height="3" fill="#2E7D32"/><line x1="2" y1="7" x2="14" y2="7" stroke="#1B5E20" stroke-width="1"/></svg>',
    stairs: '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14h3V11h3V8h3V5h3V2h0v12z" fill="#616161"/></svg>',
    escalator: '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 13 L6 9 L10 11 L14 5" stroke="#1565C0" stroke-width="2" fill="none"/><rect x="1" y="12" width="14" height="2" fill="#90CAF9"/></svg>',
    elevator: '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><rect x="4" y="1" width="8" height="14" fill="none" stroke="#2962FF" stroke-width="1.5"/><path d="M7 5 L8 7 L9 5 M7 11 L8 9 L9 11" stroke="#2962FF" stroke-width="1.2" fill="none"/></svg>'
};

/**
 * 標籤用設施小圖示（平面 / 樓梯 / 手扶梯 / 電梯）
 * @param {string[]|string} access
 * @param {string} [elevation] underground | elevated | at_grade
 */
export function getAccessIcons(access, elevation) {
    const accessList = Array.isArray(access)
        ? access
        : (typeof access === 'string' ? access.split(',').map((s) => s.trim()) : []);

    const parts = [];
    if (elevation === 'at_grade' || elevation === 'ground') {
        parts.push(`<span class="rail-ico" title="地面/平面">${SVG.ground}</span>`);
    }
    if (accessList.includes('stairs')) {
        parts.push(`<span class="rail-ico" title="樓梯">${SVG.stairs}</span>`);
    }
    if (accessList.includes('escalator')) {
        parts.push(`<span class="rail-ico" title="手扶梯">${SVG.escalator}</span>`);
    }
    if (accessList.includes('elevator')) {
        parts.push(`<span class="rail-ico" title="電梯">${SVG.elevator}</span>`);
    }
    if (!parts.length) return '';
    return `<span class="rail-access-icons" style="display:inline-flex;align-items:center;gap:4px;vertical-align:middle;">${parts.join('')}</span>`;
}

/** 出口／站牌標籤 HTML */
export function buildExitLabelHtml(name, access, elevation) {
    const elevLabel = getElevationLabel(elevation);
    const icons = getAccessIcons(access, elevation);
    return `<span style="font-size:11px;font-weight:600;white-space:nowrap;">${name} <span style="color:#666;font-weight:500;">${elevLabel}</span> ${icons}</span>`;
}

/**
 * 產生系統識別標籤
 * @param {string} sys - 系統代碼 (TRTC, KRTC, TMRT, TYMC)
 * @returns {string} HTML 字串
 */
export function getSystemBadge(sys) {
    const map = {
        'TRTC': { name: '台北捷運', color: '#2196F3' },
        'KRTC': { name: '高雄捷運', color: '#4CAF50' },
        'TMRT': { name: '台中捷運', color: '#8BC34A' },
        'TYMC': { name: '桃園捷運', color: '#9C27B0' },
        'TRA': { name: '台鐵', color: '#01579B' },
        'LTHR': { name: '高鐵', color: '#FF9800' }
    };
    const info = map[sys] || { name: sys, color: '#757575' };
    return `<span style="background:${info.color}; color:white; padding:1px 6px; border-radius:4px; font-size:10px; font-weight:bold; margin-right:5px;">${info.name}</span>`;
}

/** 萬大－中和－樹林線（在建）識別色，須與松山新店線綠區隔 */
export const RAIL_WANDA_LINE_COLOR = '#8fce5b';

/** GeoJSON 線 → Leaflet latLng 段；MultiLineString 必須全段，禁止只取 coordinates[0] */
export function geometryToLineParts(geometry) {
    if (!geometry?.coordinates) return [];
    const toLatLngs = (ring) =>
        (ring || [])
            .filter((c) => Array.isArray(c) && c.length >= 2)
            .map((c) => [c[1], c[0]]);
    if (geometry.type === 'LineString') {
        const pts = toLatLngs(geometry.coordinates);
        return pts.length >= 2 ? [pts] : [];
    }
    if (geometry.type === 'MultiLineString') {
        return geometry.coordinates
            .map((ring) => toLatLngs(ring))
            .filter((pts) => pts.length >= 2);
    }
    return [];
}

/**
 * 捷運／鐵路線識別色。優先 ref（BL/BR/O/R/G/Y/A），再線名；高捷與北捷同碼分色。
 */
export function resolveRailLineColor(props = {}) {
    const sys = String(props.sys || props._std_sys || '');
    const line = String(props.line || props._std_line || '');
    const name = String(props.name || props._std_name || '');
    const ref = String(props.ref || props._std_id || '').toUpperCase();
    const codeFromId = (ref.match(/(?:^|-)(BL|BR|LG)(?:[\dA-Z]|$)/) || ref.match(/(?:^|-)([ORYGA])(?:\d|$)/) || [])[1] || '';
    const blob = `${sys} ${line} ${name}`;

    if (sys.includes('高鐵')) return '#ff6600';
    if (sys.includes('台鐵')) return '#0055aa';

    if (ref === 'BL' || codeFromId === 'BL' || line.includes('板南') || name.includes('板南')) return '#0070bc';
    if (ref === 'BR' || codeFromId === 'BR' || line.includes('文湖') || name.includes('文湖')) return '#c48c31';
    if (ref === 'Y' || codeFromId === 'Y' || line.includes('環狀') || name.includes('環狀')) return '#ffdb00';
    if (ref === 'A' || codeFromId === 'A' || line.includes('機場') || name.includes('機場') || name.includes('機捷')) {
        return '#824ca0';
    }

    const isKaohsiung = sys.includes('高捷');
    if (ref === 'O' || codeFromId === 'O' || line.includes('橘') || name.includes('橘線')
        || line.includes('中和新蘆') || name.includes('中和新蘆')) {
        return isKaohsiung ? '#f39a00' : '#f8b61c';
    }
    if (ref === 'R' || codeFromId === 'R' || line.includes('紅線') || name.includes('紅線')
        || line.includes('淡水') || line.includes('信義')
        || name.includes('淡水') || name.includes('信義')) {
        return isKaohsiung ? '#e20a16' : '#e30022';
    }
    if (sys.includes('中捷') || ((ref === 'G' || line.includes('綠線') || name.includes('綠線') || name.includes('烏日')) && sys.includes('中捷'))) {
        return '#8fc31f';
    }
    if (ref === 'G' || codeFromId === 'G' || line.includes('松山') || line.includes('新店')
        || name.includes('松山') || name.includes('新店')) {
        return '#008659';
    }
    if (sys.includes('安坑') || name.includes('安坑') || line.includes('安坑')) return '#99d65c';
    if (sys.includes('淡海') || name.includes('淡海')) return '#d4145a';
    if (sys.includes('高輕') || name.includes('高輕') || name.includes('輕軌')) return '#78be20';
    if (line.includes('萬大') || name.includes('萬大') || codeFromId === 'LG' || /FUTURE-LG/i.test(ref)) {
        return RAIL_WANDA_LINE_COLOR;
    }

    if (blob.includes('捷運') || sys.includes('北捷') || sys.includes('高捷') || sys.includes('桃捷') || sys.includes('中捷')) {
        return '#00aa55';
    }
    return '#777777';
}

export const RAIL_MODES = ['mrt', 'lrt', 'train', 'hsr'];

/** 同名車站合併上限（公尺）。中正紀念堂種子點約 217m；台北車站機捷 A1 經台鐵／高鐵轉連。 */
export const RAIL_TRANSFER_CLUSTER_M = 320;
/** 異名但座標幾乎重疊（如「臺北」與「臺北-環島」） */
export const RAIL_NEAR_DUPLICATE_M = 50;

export function classifyRailMode(props = {}) {
    const cat = String(props._std_cat || props.cat || '');
    if (cat === 'HSR') return 'hsr';
    if (cat === 'TRA') return 'train';
    if (cat === 'LRT') return 'lrt';
    if (cat === 'Metro') return 'mrt';
    const blob = [
        props._std_sys, props.sys, props._std_line, props.line,
        props._std_name, props.name, props.system, props.operator
    ].map((v) => String(v || '')).join(' ');
    if (/高鐵|THSR|HSRL/.test(blob)) return 'hsr';
    if (/高輕|安坑|淡海|輕軌|LRT/.test(blob)) return 'lrt';
    if (/台鐵|\bTRA\b/.test(blob) && !/捷運|輕軌/.test(blob)) return 'train';
    return 'mrt';
}

export function classifyRailPhase(props = {}) {
    const status = String(props._std_status || props.status || '').toLowerCase();
    if (status === 'construction' || status.includes('在建') || status.includes('under_construction')) {
        return 'construction';
    }
    if (status === 'planned' || status.includes('規劃') || status === 'proposed') {
        return 'planned';
    }
    if (props._std_is_future === true || props._std_sys === 'FUTURE') return 'planned';
    return 'operating';
}

/** FUTURE-LG11-EXIT-1 → FUTURE-LG11 */
export function stationIdFromExitId(id) {
    const m = String(id || '').match(/^(FUTURE-[A-Z0-9-]+?)-EXIT-/i);
    return m ? m[1] : null;
}

const PHASE_RANK = { operating: 0, planned: 1, construction: 2 };

export function buildRailPhaseContext(stations = [], routes = []) {
    const phaseByStationId = {};
    const phaseByLine = {};
    const stationNameById = {};
    const bump = (map, key, phase) => {
        if (!key || !phase) return;
        if (!map[key] || PHASE_RANK[phase] > PHASE_RANK[map[key]]) map[key] = phase;
    };
    for (const f of stations) {
        const p = f.properties || f;
        const id = p._std_id;
        const phase = classifyRailPhase(p);
        bump(phaseByStationId, id, phase);
        bump(phaseByLine, p._std_line || p.line, phase);
        if (id) stationNameById[id] = p._std_name || p.name || '';
    }
    for (const f of routes) {
        const p = f.properties || f;
        bump(phaseByLine, p._std_line || p.line, classifyRailPhase(p));
    }
    return { phaseByStationId, phaseByLine, stationNameById };
}

function isRailExitProps(props = {}) {
    const id = String(props._std_id || '');
    const type = String(props._std_type || props.type || '');
    return /EXIT/i.test(id) || /exit|出口/i.test(type);
}

/** 僅出口：資料標 planned 但同站／同線已在建 → 跟車站／路線走。路線本身不繼承。 */
export function effectiveRailPhase(props = {}, ctx = {}) {
    const own = classifyRailPhase(props);
    if (!isRailExitProps(props)) return own;
    if (own === 'operating') return 'operating';
    const sid = stationIdFromExitId(props._std_id);
    const fromStation = sid ? ctx.phaseByStationId?.[sid] : null;
    const fromLine = ctx.phaseByLine?.[props._std_line || props.line];
    if (fromStation === 'construction' || fromLine === 'construction') return 'construction';
    if (fromStation === 'operating') return 'operating';
    return own;
}

export function formatExitDisplayName(props = {}, stationNameById = {}) {
    const raw = String(props._std_name || props.name || '').trim();
    const id = String(props._std_id || '');
    const sid = stationIdFromExitId(id);
    const fromStation = sid ? String(stationNameById[sid] || '').trim() : '';
    const codeFromId = (id.match(/EXIT-(.+)$/i) || [])[1] || (/^[1-9A-Za-z]{1,3}$/.test(id) ? id : '');
    const rawIsCode = /^[0-9A-Za-z]{1,3}$/.test(raw);
    const code = rawIsCode ? raw : codeFromId;
    const station = fromStation || (!rawIsCode ? raw.replace(/站出口.*$/, '').replace(/站$/, '') : '');
    if (code === '0' && station) return station;
    if (station && code && code !== '0' && !station.includes(code)) return `${station} ${code}`;
    if (station) return station;
    return raw || '出口';
}

export function railFeatureVisible(props, filters = {}, ctx) {
    const mode = classifyRailMode(props);
    const phase = ctx ? effectiveRailPhase(props, ctx) : classifyRailPhase(props);
    const modes = filters.modes || {};
    if (!modes[mode]) return false;
    if (phase === 'construction') return !!filters.construction;
    if (phase === 'planned') return !!filters.planned;
    return true;
}

const RING_COLOR_ORDER = [
    '#0070bc', '#e30022', '#008659', '#f8b61c', '#c48c31', '#ffdb00',
    '#824ca0', RAIL_WANDA_LINE_COLOR, '#e20a16', '#f39a00', '#ff6600', '#0055aa'
];

export function normalizeRailStationName(name) {
    return String(name || '')
        .trim()
        .replace(/臺/g, '台')
        .replace(/\s+/g, '')
        .replace(/車站$/, '')
        .replace(/站$/, '');
}

export function haversineMeters(a, b) {
    const R = 6371000;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const sin = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(sin)));
}

function featureLngLat(feature) {
    const c = feature?.geometry?.coordinates;
    if (!Array.isArray(c) || c.length < 2) return null;
    const lng = Number(c[0]);
    const lat = Number(c[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    return { lng, lat };
}

function pickClusterDisplayName(members) {
    const names = members
        .map((m) => String(m.props.name || m.props._std_name || '').trim())
        .filter(Boolean);
    if (!names.length) return '未知車站';
    const scored = names.map((n) => {
        let s = 0;
        if (n.includes('車站')) s += 4;
        if (!n.includes('環島')) s += 2;
        s += Math.min(n.length, 12);
        return { n, s };
    });
    scored.sort((a, b) => b.s - a.s);
    return scored[0].n;
}

function collectClusterRings(members) {
    const byColor = new Map();
    for (const m of members) {
        const color = resolveRailLineColor(m.props);
        const phase = classifyRailPhase(m.props);
        if (!byColor.has(color)) byColor.set(color, { color, phases: [] });
        byColor.get(color).phases.push(phase);
    }
    return [...byColor.values()]
        .map((row) => ({
            color: row.color,
            dashed: row.phases.every((p) => p !== 'operating')
        }))
        .sort((a, b) => {
            const ia = RING_COLOR_ORDER.indexOf(a.color);
            const ib = RING_COLOR_ORDER.indexOf(b.color);
            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
}

/**
 * 轉乘站可能多系統／多線、高程不同 → 分列；禁止用單一高程代表整站。
 * @param {object[]} members GeoJSON features 或 properties
 * @returns {{ mode: 'empty'|'single'|'multi', elev?: string, label?: string, rows: {sys:string,line:string,phaseZh:string,elev:string,elevZh:string}[] }}
 */
export function summarizeStationElevations(members) {
    const rows = [];
    const seen = new Set();
    for (const f of members || []) {
        const p = f?.properties || f || {};
        const sys = String(p.sys || p._std_sys || '').trim();
        const line = String(p.line || p._std_line || '').trim();
        const elev = p._std_elevation || p.elevation || 'unknown';
        const phase = classifyRailPhase(p);
        const phaseZh = phase === 'construction' ? '在建' : phase === 'planned' ? '規劃' : '營運';
        const key = `${sys}|${line}|${elev}|${phaseZh}`;
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({
            sys,
            line,
            phaseZh,
            elev,
            elevZh: getElevationLabel(elev)
        });
    }
    if (!rows.length) return { mode: 'empty', rows: [] };
    const elevSet = new Set(rows.map((r) => r.elev));
    if (elevSet.size === 1 && !elevSet.has('unknown')) {
        return { mode: 'single', elev: rows[0].elev, label: rows[0].elevZh, rows };
    }
    return { mode: 'multi', rows };
}

/**
 * 站 popup 路線列（含逐線高程）
 * @param {object[]} members
 * @returns {string[]} 已 escape 前的純文字列（呼叫端 esc）
 */
export function buildStationMemberLines(members) {
    const { rows } = summarizeStationElevations(members);
    return rows.map((r) => {
        const parts = [r.sys, r.line, r.phaseZh, r.elevZh].filter(Boolean);
        return parts.join(' · ');
    });
}

/**
 * 可見車站按站名＋距離合成轉乘點。顯示層聚合，不改熱層 ID。
 * @returns {{ lng: number, lat: number, name: string, rings: {color:string,dashed:boolean}[], members: object[], properties: object }[]}
 */
export function clusterRailStations(features, opts = {}) {
    const maxM = opts.maxMeters ?? RAIL_TRANSFER_CLUSTER_M;
    const nearM = opts.nearDuplicateMeters ?? RAIL_NEAR_DUPLICATE_M;
    const items = [];
    for (const feature of features || []) {
        const ll = featureLngLat(feature);
        if (!ll) continue;
        const props = feature.properties || {};
        items.push({
            feature,
            props,
            ...ll,
            nameKey: normalizeRailStationName(props.name || props._std_name)
        });
    }
    const n = items.length;
    const parent = items.map((_, i) => i);
    const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
    const unite = (i, j) => {
        const a = find(i);
        const b = find(j);
        if (a !== b) parent[a] = b;
    };
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            const d = haversineMeters(items[i], items[j]);
            if (d <= nearM) unite(i, j);
            else if (items[i].nameKey && items[i].nameKey === items[j].nameKey && d <= maxM) {
                unite(i, j);
            }
        }
    }
    const groups = new Map();
    for (let i = 0; i < n; i++) {
        const root = find(i);
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root).push(items[i]);
    }
    return [...groups.values()].map((members) => {
        const operating = members.filter((m) => classifyRailPhase(m.props) === 'operating');
        const forCentroid = operating.length ? operating : members;
        const lng = forCentroid.reduce((s, m) => s + m.lng, 0) / forCentroid.length;
        const lat = forCentroid.reduce((s, m) => s + m.lat, 0) / forCentroid.length;
        const rings = collectClusterRings(members);
        const name = pickClusterDisplayName(members);
        const primary = (operating[0] || members[0]).props;
        return {
            lng,
            lat,
            name,
            rings,
            members: members.map((m) => m.feature),
            properties: primary
        };
    });
}

/**
 * 圓點＋路線色同心外框。線愈多框愈多，轉乘站自然較大。
 * @returns {{ html: string, size: number }}
 */
export function buildRailStationMarkerHtml(rings = []) {
    const list = rings.length ? rings : [{ color: '#50ff96', dashed: false }];
    const coreR = 4.5;
    const stroke = 3;
    const gap = 0.7;
    const n = list.length;
    const outerR = coreR + gap + n * stroke + (n - 1) * gap;
    const size = Math.ceil((outerR + stroke / 2 + 2) * 2);
    const c = size / 2;
    const parts = [];
    for (let i = n - 1; i >= 0; i--) {
        const r = coreR + gap + (i + 0.5) * stroke + i * gap;
        const dash = list[i].dashed ? ` stroke-dasharray="${stroke * 1.35} ${stroke * 0.9}"` : '';
        parts.push(
            `<circle cx="${c}" cy="${c}" r="${r.toFixed(2)}" fill="none" stroke="${list[i].color}" stroke-width="${stroke}"${dash}/>`
        );
    }
    parts.push(
        `<circle cx="${c}" cy="${c}" r="${coreR}" fill="#f7fff9" stroke="#111" stroke-width="1.2"/>`
    );
    return {
        html: `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" aria-hidden="true">${parts.join('')}</svg>`,
        size
    };
}
